/**
 * GameActionsService - Handles core gameplay actions
 *
 * This service contains the complex logic for word selection, giving up,
 * backtracking, and game completion. It coordinates between gameplay state
 * and external services.
 */

import { herState } from "../../ariadne/gameplay";
import { useAriadneLearned } from "../../ariadne/learned";
import { nextRecord, saveState } from "../../ariadne/persist";
import { ariadneReport } from "../../ariadne/report";
import { ariadneSeat, ARIADNE_MODEL } from "../../ariadne/seat";
import { bestRoutePath } from "../../utils/graphUtils";
import { generateGameReport, judgeMove } from "../../utils/gameReportUtils";
import {
  applyCollectionAwards,
  evaluateAchievements,
  handleProgressiveAchievements,
  applyAtlasAwards,
} from "../../features/achievements";
import { dailyChallengesService } from "../../services/DailyChallengesService";
import {
  getLoadedGraphIdentity,
  getLoadedGraphVersion,
} from "../../services/dataLoader";
import { unifiedDataStore } from "../../services/UnifiedDataStore";
import { useGameplayStore } from "../stores/useGameplayStore";
import { useGameFlowStore } from "../stores/useGameFlowStore";
import { useAssetStore } from "../../stores/useAssetStore";
import { useDailyChallengeStore } from "../../stores/useDailyChallengeStore";
import type { PotentialRarestMove } from "../gameLogic";
import type { BacktrackReportEntry } from "../../utils/gameReportUtils";
import { buildGameCompletedEvent } from "../telemetryEvent";
import { buildMoveRecord } from "../moveRecord";
import { isSandboxed } from "../../dev/devSession";
import { presentGameReport } from "../winCelebration";

class GameActionsService {
  /** A player action in flight; a second tap meanwhile is ignored, never applied out of order. */
  private inFlight = false;

  /**
   * Her turn (spec §3.1); a seat failure is logged and never costs the player
   * the move. In the tutorial she follows the player (§3.1a).
   */
  private async herTurn(
    graphData: Record<string, { edges: Record<string, number> }>,
    pair: { start: string; target: string },
    action: Parameters<typeof ariadneSeat.commit>[2],
  ) {
    try {
      return await ariadneSeat.commit(graphData, pair, action, {
        follow: useGameplayStore.getState().isTutorial,
      });
    } catch (error) {
      console.error(
        "ariadne: her turn failed; the game goes on without it:",
        error,
      );
      return null;
    }
  }

  /**
   * Handle word selection during gameplay
   */
  async selectWord(selectedWord: string): Promise<void> {
    if (this.inFlight) return;
    this.inFlight = true;
    try {
      await this.selectWordNow(selectedWord);
    } finally {
      this.inFlight = false;
    }
  }

  private async selectWordNow(selectedWord: string): Promise<void> {
    const gameplayStore = useGameplayStore;
    const _gameFlowStore = useGameFlowStore.getState();
    const { graphData, wordFrequencies } = useAssetStore.getState();

    const {
      currentWord,
      targetWord,
      playerPath,
      optimalPath: goldPath,
      gameStatus,
      backtrackHistory: _backtrackHistory,
      isChallenge: _isChallenge,
      isDailyChallenge: _isDailyChallenge,
      currentDailyChallengeId: _currentDailyChallengeId,
      potentialRarestMovesThisGame,
      aiPath,
    } = gameplayStore.getState();

    // Validation
    if (!graphData || !currentWord || !targetWord || gameStatus !== "playing") {
      return;
    }

    if (!graphData[currentWord]?.edges[selectedWord]) {
      return;
    }

    // Track rarest moves for achievements
    const newRarestMoves = wordFrequencies
      ? this.updateRarestMoves(
          graphData,
          currentWord,
          selectedWord,
          wordFrequencies,
          potentialRarestMovesThisGame,
        )
      : potentialRarestMovesThisGame;

    // Judge the move against the gold path and the best routes to the target.
    const moveJudgement = judgeMove(
      graphData,
      currentWord,
      selectedWord,
      goldPath,
      targetWord,
      wordFrequencies,
    );

    const newPlayerPath = [...playerPath, selectedWord];
    const won = selectedWord === targetWord;
    const localOptimalRoute = won
      ? []
      : bestRoutePath(graphData, selectedWord, targetWord);

    // Her turn with the player's (spec §3.1, D2): the player's action is
    // committed, hers revealed with it, and both land in one state update so
    // her thread never renders ahead of the move.
    const reveal = await this.herTurn(
      graphData,
      { start: playerPath[0], target: targetWord },
      { kind: "move", word: selectedWord },
    );
    gameplayStore.getState().setGameState({
      currentWord: selectedWord,
      playerPath: newPlayerPath,
      ...herState(reveal, aiPath),
    });

    gameplayStore.getState().addMoveJudgement(moveJudgement);
    gameplayStore
      .getState()
      .addMoveRecord(
        buildMoveRecord(
          moveJudgement,
          graphData,
          wordFrequencies,
          targetWord,
          localOptimalRoute,
        ),
      );
    gameplayStore.getState().updatePotentialRarestMoves(newRarestMoves);

    // Check for game completion
    if (won) {
      await this.handleGameCompletion("won", newPlayerPath);
      return;
    }

    // Update the Local Optimal route and save the game
    gameplayStore
      .getState()
      .setGameState({ suggestedPathFromCurrent: localOptimalRoute });

    await this.saveCurrentGameState();
  }

  /**
   * Handle giving up the current game
   */
  async giveUp(): Promise<void> {
    if (this.inFlight) return;
    this.inFlight = true;
    try {
      const gameplayStore = useGameplayStore;
      const { playerPath, targetWord, gameStatus } = gameplayStore.getState();

      // Prevent action if a game is not in progress
      if (gameStatus !== "playing") {
        return;
      }

      // Giving up is a turn too (spec D1): it ends the match and cuts her.
      const { graphData } = useAssetStore.getState();
      if (graphData && targetWord) {
        await this.herTurn(
          graphData,
          { start: playerPath[0], target: targetWord },
          { kind: "give_up" },
        );
      }
      await this.handleGameCompletion("given_up", playerPath);
    } finally {
      this.inFlight = false;
    }
  }

  /**
   * Backtrack to a previous word in the path
   */
  async backtrackToWord(word: string, index: number): Promise<void> {
    if (this.inFlight) return;
    this.inFlight = true;
    try {
      await this.backtrackNow(word, index);
    } finally {
      this.inFlight = false;
    }
  }

  private async backtrackNow(word: string, index: number): Promise<void> {
    const gameplayStore = useGameplayStore;
    const { graphData } = useAssetStore.getState();

    const {
      playerPath,
      targetWord,
      startWord,
      currentWord: wordPlayerIsJumpingFrom,
      backtrackHistory,
      potentialRarestMovesThisGame,
      optimalChoices,
      aiPath,
    } = gameplayStore.getState();

    // Validation
    if (
      !graphData ||
      !playerPath ||
      !targetWord ||
      !startWord ||
      !wordPlayerIsJumpingFrom
    ) {
      return;
    }

    // Check if already backtracked to this word
    const alreadyBacktracked = backtrackHistory.some(
      (event) => event.landedOn === word,
    );
    if (alreadyBacktracked) {
      return;
    }

    // Validate checkpoint choice
    const checkpointChoiceIndex = index - 1;
    if (
      checkpointChoiceIndex < 0 ||
      checkpointChoiceIndex >= optimalChoices.length
    ) {
      return;
    }

    const choiceToMark = optimalChoices[checkpointChoiceIndex];
    if (
      !(choiceToMark.isGlobalOptimal || choiceToMark.isLocalOptimal) ||
      choiceToMark.usedAsCheckpoint
    ) {
      return;
    }

    // Update game state for backtrack
    const newPlayerPath = playerPath.slice(0, index + 1);
    const numMoves = newPlayerPath.length - 1;

    // Truncate tracking arrays
    const newMoveJudgements = optimalChoices.slice(0, numMoves).map((c, i) => {
      if (i === checkpointChoiceIndex) {
        return { ...c, usedAsCheckpoint: true };
      }
      return c;
    });

    const newPotentialRarestMoves = potentialRarestMovesThisGame.slice(
      0,
      numMoves,
    );

    // The gold path stays fixed at game start; only the Local Optimal route
    // is recomputed from the word landed on.
    const newCurrentWord = newPlayerPath[newPlayerPath.length - 1];
    const localOptimalRoute = bestRoutePath(
      graphData,
      newCurrentWord,
      targetWord,
    );

    // Create backtrack event
    const backtrackEvent: BacktrackReportEntry = {
      jumpedFrom: wordPlayerIsJumpingFrom,
      landedOn: word,
    };

    // A backtrack is a turn for her too (spec §3.4).
    const reveal = await this.herTurn(
      graphData,
      { start: playerPath[0], target: targetWord },
      { kind: "back", word },
    );

    // Update all state
    gameplayStore.getState().setGameState({
      currentWord: newCurrentWord,
      playerPath: newPlayerPath,
      suggestedPathFromCurrent: localOptimalRoute,
      optimalChoices: newMoveJudgements,
      ...herState(reveal, aiPath),
    });

    gameplayStore
      .getState()
      .updatePotentialRarestMoves(newPotentialRarestMoves);
    gameplayStore.getState().addBacktrackEvent(backtrackEvent);

    await this.saveCurrentGameState();
  }

  /**
   * Private: Handle game completion (win or give up)
   */
  private async handleGameCompletion(
    result: "won" | "given_up",
    finalPlayerPath: string[],
  ): Promise<void> {
    const gameplayStore = useGameplayStore;
    const _gameFlowStore = useGameFlowStore.getState();
    const { graphData } = useAssetStore.getState();

    const {
      targetWord,
      optimalPath: goldPath,
      optimalChoices,
      backtrackHistory,
      moveHistory,
      potentialRarestMovesThisGame,
      isChallenge,
      isDailyChallenge,
      isTutorial,
      currentDailyChallengeId,
      aiPath,
      aiModel,
    } = gameplayStore.getState();

    if (!graphData || !targetWord) return;

    // Dev sandbox: the game plays out and reports, but nothing is persisted
    // (saved game, history, achievements, daily progress) or sent.
    const sandbox = isSandboxed();

    // The match is over (spec §3.2): she learns from the player's turns (D7)
    // and her state is persisted; the report carries her thread. In the
    // tutorial she followed, so what she learns is exactly the player's route
    // (D10, 2026-10-27).
    const ended = ariadneSeat.end();
    const taught = !!ended?.taught;
    if (taught) useAriadneLearned.getState().bump();
    if (ended && taught && !sandbox) {
      try {
        await saveState(ended.learned);
      } catch (error) {
        console.error("ariadne: could not save her state:", error);
      }
    }
    const herPath = ended ? ended.outcome.thread : aiPath;
    const herModel = ended ? ARIADNE_MODEL : aiModel;

    // Clear saved game
    if (!sandbox) {
      await unifiedDataStore.clearCurrentGame(
        isChallenge ? "challenge" : "regular",
      );
    }

    // Generate game report
    const report = generateGameReport(
      graphData,
      finalPlayerPath,
      goldPath,
      optimalChoices,
      targetWord,
      bestRoutePath,
      backtrackHistory,
      potentialRarestMovesThisGame,
      isDailyChallenge,
      currentDailyChallengeId,
      herPath,
      herModel,
      isChallenge,
      isTutorial,
    );
    report.moveHistory = moveHistory;
    if (ended) {
      report.ariadne = ariadneReport(ended.outcome);
    }
    // The record counts every game she raced (spec D10); a followed game
    // (the tutorial) has no verdict to count.
    if (ended && taught && !ended.outcome.followed && !sandbox) {
      try {
        const { record } = await unifiedDataStore.getAriadne();
        const next = nextRecord(record, ended.outcome.verdict);
        await unifiedDataStore.setAriadneRecord(next);
        report.ariadneGamesAhead = next.ahead + next.left_behind;
      } catch (error) {
        console.error("ariadne: could not update her record:", error);
      }
    }

    // Evaluate achievements. The tutorial is guided, so it only earns Q.E.D.
    // (on a win) and skips progressive counters; see features/achievements.
    const earnedAchievements = evaluateAchievements(report, result);
    report.earnedAchievements = earnedAchievements;

    // Persist achievements; remember which are first-time unlocks so the
    // report can lead with them.
    const newAchievementIds: string[] = [];
    if (!sandbox) {
      for (const achievement of earnedAchievements) {
        if (await unifiedDataStore.unlockAchievement(achievement.id)) {
          newAchievementIds.push(achievement.id);
        }
      }
    }
    report.newAchievementIds = newAchievementIds;
    if (!sandbox) await handleProgressiveAchievements(report, result);
    if (!sandbox) {
      await applyCollectionAwards(report, finalPlayerPath, graphData);
    }
    // The atlas milestones read her state after learning (spec 2026-10-20
    // §3.4); the tutorial is guided and earns only Q.E.D., so a followed
    // match never awards one.
    if (ended && taught && !ended.outcome.followed && !sandbox) {
      await applyAtlasAwards(
        report,
        ended.learned.seen,
        Object.keys(graphData).length,
      );
    }

    // Update game state
    gameplayStore.getState().setGameState({
      gameStatus: result,
      gameReport: report,
      aiPath: herPath,
      ariadneStatus: ended
        ? ended.outcome.status
        : gameplayStore.getState().ariadneStatus,
    });

    // After a give-up the gold path is the most useful overlay, so it is on
    // by default. Her thread keeps the player's preference (spec D4).
    gameplayStore.getState().setPathDisplayMode({
      player: true,
      optimal: result === "given_up",
      suggested: false,
      ai: gameplayStore.getState().pathDisplayMode.ai,
    });

    // Show the report: a win opens it when the board's win wave finishes.
    presentGameReport(report, result);
    if (sandbox) return;

    // Add to history (every mode, the tutorial included). A failed save is
    // logged; telemetry and daily progress below still run.
    try {
      await unifiedDataStore.addGameToHistory(report);
    } catch (error) {
      console.error("Failed to save game to history:", error);
    }

    // Track telemetry event (fire-and-forget)
    try {
      const { appInitializer } = await import("../../services/AppInitializer");
      if (appInitializer.telemetryService) {
        appInitializer.telemetryService.track(
          buildGameCompletedEvent(report, result, finalPlayerPath, {
            version: getLoadedGraphVersion(),
            identity: getLoadedGraphIdentity(),
          }),
        );
        appInitializer.telemetryService.flush().catch(() => {});
      }
    } catch {
      // Telemetry failure never blocks gameplay
    }

    // Handle daily challenge completion
    if (isDailyChallenge && currentDailyChallengeId) {
      try {
        await dailyChallengesService.saveDailyChallengeProgress(
          currentDailyChallengeId,
          {
            status: result,
            completedAt: new Date().toISOString(),
            playerMoves: finalPlayerPath.length - 1,
            playerPath: finalPlayerPath,
            ariadneVerdict: ended?.outcome.verdict,
            ariadneMoves: ended?.outcome.her.moves,
            ariadneFinished: ended?.outcome.her.finished,
          },
        );
        useDailyChallengeStore.getState().markChallengeCompleted();

        // REWARD: Grant additional free games for completing daily challenge
        console.log(
          "🎯 Daily challenge completed! Granting 5 additional free games as reward.",
        );
        const currentFreeGames = await unifiedDataStore.getRemainingFreeGames();
        await unifiedDataStore.setFreeGamesRemaining(currentFreeGames + 5);

        // Update the store state to reflect the new free games
        await useDailyChallengeStore.getState().refreshGameAccessState();
      } catch (error) {
        console.error("Failed to save daily challenge progress:", error);
      }
    }
  }

  /**
   * Private: Update rarest moves tracking for achievements
   */
  private updateRarestMoves(
    graphData: Record<string, { edges: Record<string, number> }>,
    currentWord: string,
    selectedWord: string,
    wordFrequencies: Record<string, number>,
    currentRarestMoves: PotentialRarestMove[],
  ): PotentialRarestMove[] {
    let rarestOfferedThisStep: { word: string; frequency: number } | null =
      null;

    const directNeighborsEdges = graphData[currentWord]?.edges;

    if (
      directNeighborsEdges &&
      Object.keys(directNeighborsEdges).length > 0 &&
      wordFrequencies
    ) {
      const neighborFreqDetails = Object.keys(directNeighborsEdges)
        .map((neighborWord) => ({
          word: neighborWord,
          frequency: wordFrequencies[neighborWord] ?? Infinity,
        }))
        .sort((a, b) => a.frequency - b.frequency);

      if (
        neighborFreqDetails.length > 0 &&
        neighborFreqDetails[0].frequency !== Infinity
      ) {
        rarestOfferedThisStep = neighborFreqDetails[0];
      }
    }

    const newRarestMoves = [...currentRarestMoves];
    if (rarestOfferedThisStep) {
      newRarestMoves.push({
        word: rarestOfferedThisStep.word,
        frequency: rarestOfferedThisStep.frequency,
        playerChoseThisRarestOption:
          selectedWord === rarestOfferedThisStep.word,
      });
    }

    return newRarestMoves;
  }

  /**
   * Private: Save current game state to storage
   */
  private async saveCurrentGameState(): Promise<void> {
    if (isSandboxed()) return;
    const gameplayStore = useGameplayStore;

    const {
      startWord,
      targetWord,
      currentWord,
      playerPath,
      optimalPath,
      suggestedPathFromCurrent,
      pathDisplayMode,
      optimalChoices,
      backtrackHistory,
      moveHistory,
      potentialRarestMovesThisGame,
      isChallenge,
      isDailyChallenge,
      isTutorial,
      aiPath,
      aiModel,
      currentDailyChallengeId,
    } = gameplayStore.getState();

    if (!startWord || !targetWord || !currentWord) return;

    const gameStateToSave = {
      startWord,
      targetWord,
      currentWord,
      playerPath,
      optimalPath,
      suggestedPathFromCurrent,
      gameStatus: "playing" as const,
      optimalChoices,
      backtrackHistory,
      moveHistory,
      potentialRarestMovesThisGame,
      pathDisplayMode,
      isChallenge,
      isDailyChallenge,
      isTutorial,
      aiPath,
      aiModel,
      currentDailyChallengeId,
      match: ariadneSeat.snapshot() ?? undefined,
      startTime: Date.now(),
    };

    await unifiedDataStore.saveCurrentGame(
      gameStateToSave,
      isChallenge ? "challenge" : "regular",
    );
  }
}

// Export singleton instance
export const gameActionsService = new GameActionsService();
